import React, { useState, useEffect, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
  IconButton,
  Chip,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import {
  ArrowBack as BackIcon,
  GetApp as DownloadIcon,
  BarChart as ChartIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@material-ui/icons";
import { DataPulseContext } from "../contexts/DataPulseContext";
import { ScreenContext } from "../contexts/ScreenContext";
import { makeStyles } from "@material-ui/core/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    minHeight: "100vh",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  statsRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  statChip: {
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  questionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#fff',
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
  },
  questionTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  questionMeta: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  chartContainer: {
    height: 200,
    marginTop: theme.spacing(1),
  },
  optionChip: {
    margin: theme.spacing(0.5),
    marginRight: theme.spacing(1),
    fontWeight: 500,
  },
  compactTable: {
    '& .MuiTableCell-root': {
      padding: '6px 8px',
      fontSize: '0.8rem',
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[100],
    },
  },
  answerCell: {
    maxWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ratingDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  ratingAvg: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
}));

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82ca9d"];

// Separate component for text-based answers (uses its own hooks per question)
const TextQuestionAnalytics = ({ question, questionAnalytics, classes }) => {
  const textAnswers = questionAnalytics.text_answers || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [accordionExpanded, setAccordionExpanded] = useState(false);

  const filteredAnswers = searchTerm
    ? textAnswers.filter((answer) => answer.toLowerCase().includes(searchTerm.toLowerCase()))
    : textAnswers;

  return (
    <Accordion 
      expanded={accordionExpanded} 
      onChange={(e, isExpanded) => setAccordionExpanded(isExpanded)}
      style={{ marginBottom: 16 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mr={2}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {question.display_order}. {question.question_text}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={`${textAnswers.length} responses`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block', padding: 16 }}>
        {textAnswers.length > 5 && (
          <TextField
            fullWidth
            size="small"
            placeholder="Search responses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: 12 }}
            InputProps={{
              startAdornment: (
                <Box mr={1}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                </Box>
              ),
            }}
          />
        )}

        {filteredAnswers.length > 0 ? (
          <Box 
            maxHeight={400} 
            overflow="auto"
            style={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: 4, 
              padding: 8,
              backgroundColor: '#fafafa'
            }}
          >
            {filteredAnswers.map((answer, index) => (
              <Box
                key={index}
                mb={1}
                p={1.5}
                border="1px solid #e0e0e0"
                borderRadius={4}
                style={{ 
                  wordWrap: "break-word", 
                  maxWidth: "100%",
                  backgroundColor: '#fff'
                }}
              >
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                  Response #{index + 1}
                </Typography>
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                  {answer}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic", padding: 16, textAlign: 'center' }}>
            {searchTerm ? 'No responses match your search.' : 'No responses yet.'}
          </Typography>
        )}

        {searchTerm && filteredAnswers.length < textAnswers.length && (
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8, textAlign: 'center' }}>
            Showing {filteredAnswers.length} of {textAnswers.length} responses
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const SurveyAnalytics = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { loadSurvey, loadSurveys, currentSurvey, getSurveyAnalytics, getSurveyResponses, deleteResponse } = useContext(DataPulseContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  const [analytics, setAnalytics] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, responseId: null });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      await loadSurvey(id);
      const analyticsData = await getSurveyAnalytics(id);
      const responsesData = await getSurveyResponses(id);
      
      setAnalytics(analyticsData);
      setResponses(responsesData);
      setLoading(false);
    } catch (error) {
      console.error("Error loading analytics:", error);
      setLoading(false);
    }
  };

  const handleDeleteClick = (responseId) => {
    setDeleteDialog({ open: true, responseId });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteResponse(id, deleteDialog.responseId);
      setSnackbarMessage({ message: 'Response deleted successfully', severity: 'success' });
      setDeleteDialog({ open: false, responseId: null });
      // Reload data to update stats and response list
      loadData();
      // Also reload surveys list to update KPIs on DataPulse landing page
      loadSurveys();
    } catch (error) {
      console.error('Error deleting response:', error);
      setSnackbarMessage({ message: 'Failed to delete response', severity: 'error' });
      setDeleteDialog({ open: false, responseId: null });
    }
  };

  const exportToCSV = () => {
    if (!currentSurvey || !responses) return;

    // Create CSV header
    const headers = ["Response ID", "Recipient Email", "Started At", "Completed At", "IP Address"];
    currentSurvey.questions.forEach((q) => {
      headers.push(`Q${q.display_order}: ${q.question_text}`);
    });

    // Create CSV rows
    const rows = responses.map((response) => {
      const row = [
        response.id,
        response.recipient_email || "Anonymous",
        response.started_at,
        response.completed_at || "Not completed",
        response.ip_address || "N/A",
      ];

      currentSurvey.questions.forEach((question) => {
        const answer = response.answers.find((a) => a.question_id === question.id);
        if (answer) {
          if (answer.answer_text) {
            row.push(answer.answer_text);
          } else if (answer.selected_option_text) {
            row.push(answer.selected_option_text);
          } else if (answer.selected_options) {
            row.push(answer.selected_options.join(", "));
          } else if (answer.numeric_value !== null) {
            row.push(answer.numeric_value);
          } else {
            row.push("");
          }
        } else {
          row.push("");
        }
      });

      return row;
    });

    // Combine into CSV string
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Download
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `survey-${id}-responses-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const renderQuestionAnalytics = (question) => {
    const questionAnalytics = analytics?.questions_analytics?.find((q) => q.question_id === question.id);
    if (!questionAnalytics) return null;

    switch (question.question_type) {
      case "multiple_choice":
      case "dropdown":
      case "yes_no": {
        const data = questionAnalytics.option_counts?.map((opt, idx) => ({
          name: opt.option_text,
          count: opt.count,
          percentage: ((opt.count / questionAnalytics.response_count) * 100).toFixed(1),
          color: COLORS[idx % COLORS.length],
        })) || [];
        
        // Debug: log if data is empty
        if (data.length === 0) {
          console.warn('Empty data for question:', question.question_text, questionAnalytics);
        }

        return (
          <Paper className={classes.questionCard}>
            <Box className={classes.questionHeader}>
              <Box flex={1}>
                <Typography className={classes.questionTitle}>
                  Q{question.display_order}. {question.question_text}
                </Typography>
                <Box className={classes.questionMeta}>
                  <Chip 
                    label={`${questionAnalytics.response_count} responses`} 
                    size="small" 
                    color="primary"
                    variant="outlined"
                  />
                  {data.map((item) => (
                    <Chip
                      key={item.name}
                      label={`${item.name}: ${item.count} (${item.percentage}%)`}
                      size="small"
                      className={classes.optionChip}
                      style={{ 
                        backgroundColor: item.color, 
                        color: '#fff',
                        fontSize: '0.75rem'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {data.length > 0 ? (
              <ResponsiveContainer width="100%" height={200} className={classes.chartContainer}>
                <BarChart 
                  data={data} 
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  barCategoryGap="10%"
                  barSize={50}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 11 }}
                    stroke="#666"
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    stroke="#666"
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ fontSize: '0.85rem' }}
                    formatter={(value, name, props) => [
                      `${value} (${props.payload.percentage}%)`,
                      'Responses'
                    ]}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ padding: 20, textAlign: 'center' }}>
                No responses yet
              </Typography>
            )}
          </Paper>
        );
      }

      case "checkboxes": {
        const totalSelections = questionAnalytics.option_counts?.reduce((sum, opt) => sum + opt.count, 0) || 0;
        
        const data = questionAnalytics.option_counts?.map((opt, idx) => ({
          name: opt.option_text,
          count: opt.count,
          percentage: totalSelections === 0 ? 0 : ((opt.count / totalSelections) * 100).toFixed(1),
          color: COLORS[idx % COLORS.length],
        })) || [];

        return (
          <Paper className={classes.questionCard}>
            <Box className={classes.questionHeader}>
              <Box flex={1}>
                <Typography className={classes.questionTitle}>
                  Q{question.display_order}. {question.question_text}
                </Typography>
                <Box className={classes.questionMeta}>
                  <Chip 
                    label={`${questionAnalytics.response_count} responses`} 
                    size="small" 
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    label={`${totalSelections} total selections`} 
                    size="small" 
                    variant="outlined"
                  />
                  {data.map((item) => (
                    <Chip
                      key={item.name}
                      label={`${item.name}: ${item.count} (${item.percentage}%)`}
                      size="small"
                      className={classes.optionChip}
                      style={{ 
                        backgroundColor: item.color, 
                        color: '#fff',
                        fontSize: '0.75rem'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {data.length > 0 ? (
              <ResponsiveContainer width="100%" height={200} className={classes.chartContainer}>
                <BarChart 
                  data={data} 
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  barCategoryGap="10%"
                  barSize={50}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 11 }}
                    stroke="#666"
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    stroke="#666"
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ fontSize: '0.85rem' }}
                    formatter={(value, name, props) => [
                      `${value} (${props.payload.percentage}%)`,
                      'Responses'
                    ]}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ padding: 20, textAlign: 'center' }}>
                No responses yet
              </Typography>
            )}
          </Paper>
        );
      }

      case "rating": {
        const avgRating = questionAnalytics.average_rating?.toFixed(1) || 0;
        const distribution = questionAnalytics.rating_distribution || [];
        const maxScale = question.rating_scale || 5;
        
        // Generate star rating display
        const fullStars = Math.floor(avgRating);
        const hasHalfStar = avgRating % 1 >= 0.5;

        return (
          <Paper className={classes.questionCard}>
            <Box className={classes.questionHeader}>
              <Box flex={1}>
                <Typography className={classes.questionTitle}>
                  Q{question.display_order}. {question.question_text}
                </Typography>
                <Box className={classes.questionMeta}>
                  <Chip 
                    label={`${questionAnalytics.response_count} responses`} 
                    size="small" 
                    color="primary"
                    variant="outlined"
                  />
                  <Box className={classes.ratingDisplay}>
                    <Typography className={classes.ratingAvg}>
                      {avgRating}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginLeft: 4 }}>
                      {'⭐'.repeat(fullStars)}{hasHalfStar ? '½' : ''} / {maxScale}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {distribution.length > 0 && (
              <ResponsiveContainer width="100%" height={200} className={classes.chartContainer}>
                <BarChart 
                  data={distribution}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  barCategoryGap="10%"
                  barSize={50}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="rating" 
                    tick={{ fontSize: 11 }}
                    stroke="#666"
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                    stroke="#666"
                  />
                  <Tooltip contentStyle={{ fontSize: '0.85rem' }} />
                  <Bar dataKey="count" fill="#FFBB28" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        );
      }

      case "text":
      case "textarea":
      case "email":
      case "phone":
      case "date":
        return <TextQuestionAnalytics question={question} questionAnalytics={questionAnalytics} classes={classes} />;

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box className={classes.root}>
        <Typography>Loading analytics...</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => history.push("/data-pulse")} size="small" style={{ marginRight: 8 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5">{currentSurvey?.name || "Survey Analytics"}</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={exportToCSV}
        >
          Export CSV
        </Button>
      </Box>

      {currentSurvey?.description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          {currentSurvey.description}
        </Typography>
      )}

      <Box className={classes.statsRow}>
        <Chip 
          className={classes.statChip}
          icon={<ChartIcon style={{ color: 'white' }} />}
          label={`${analytics?.total_sent || 0} Sent`}
          color="primary"
          style={{ fontWeight: 600 }}
        />
        <Chip 
          className={classes.statChip}
          label={`${analytics?.total_started || 0} Started`}
          variant="outlined"
          color="primary"
        />
        <Chip 
          className={classes.statChip}
          label={`${analytics?.total_completed || 0} Completed`}
          variant="outlined"
          color="primary"
        />
        <Chip 
          className={classes.statChip}
          label={`${analytics?.completion_rate || 0}% Completion Rate`}
          variant="outlined"
        />
      </Box>

      <Typography variant="h6" style={{ marginTop: 24, marginBottom: 12, fontWeight: 600 }}>
        Question Analytics
      </Typography>

      {currentSurvey?.questions?.map((question) => (
        <Box key={question.id}>{renderQuestionAnalytics(question)}</Box>
      ))}

      <Typography variant="h6" style={{ marginTop: 24, marginBottom: 12, fontWeight: 600 }}>
        Individual Responses ({responses.length})
      </Typography>

      <TableContainer component={Paper}>
        <Table className={classes.compactTable} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Recipient</TableCell>
              <TableCell>Completed</TableCell>
              {currentSurvey?.questions?.map((q) => (
                <TableCell key={q.id} className={classes.answerCell}>
                  Q{q.display_order}
                </TableCell>
              ))}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {responses.map((response) => (
              <TableRow key={response.id}>
                <TableCell>{response.recipient_email || "Anonymous"}</TableCell>
                <TableCell>
                  {response.completed_at ? (
                    <Chip
                      label={new Date(response.completed_at).toLocaleDateString()}
                      color="primary"
                      size="small"
                    />
                  ) : (
                    <Chip label="In Progress" size="small" />
                  )}
                </TableCell>
                {currentSurvey?.questions?.map((question) => {
                  const answer = response.answers?.find((a) => a.question_id === question.id);
                  let displayValue = "-";
                  let displayTitle = "-";
                  
                  if (answer) {
                    // Text-based answers (text, textarea, email, phone, date)
                    if (answer.answer_text) {
                      displayValue = answer.answer_text;
                      displayTitle = answer.answer_text;
                    } 
                    // Checkbox question with multiple selections
                    else if (answer.selected_options && Array.isArray(answer.selected_options) && answer.selected_options.length > 0) {
                      displayTitle = answer.selected_options.join(", ");
                      displayValue = answer.selected_options.length > 2 
                        ? `${answer.selected_options.slice(0, 2).join(", ")}... (+${answer.selected_options.length - 2})`
                        : displayTitle;
                    } 
                    // Single selection (multiple choice, dropdown, yes/no)
                    else if (answer.selected_option_text) {
                      displayValue = answer.selected_option_text;
                      displayTitle = answer.selected_option_text;
                    } 
                    // Rating question
                    else if (answer.numeric_value !== null && answer.numeric_value !== undefined) {
                      displayValue = `${answer.numeric_value}${question.rating_scale ? `/${question.rating_scale}` : ''}`;
                      displayTitle = displayValue;
                    }
                  }
                  
                  return (
                    <TableCell key={question.id} className={classes.answerCell} title={displayTitle}>
                      {displayValue}
                    </TableCell>
                  );
                })}
                <TableCell align="center">
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteClick(response.id)}
                    color="secondary"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, responseId: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Response</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this response? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, responseId: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurveyAnalytics;
